import * as THREE from 'three';
import { PlayerBike } from '../entities/PlayerBike';

export enum CameraMode {
  FIRST_PERSON,
  THIRD_PERSON,
}

export class CameraController {
  private camera: THREE.PerspectiveCamera;
  private mode: CameraMode = CameraMode.THIRD_PERSON;
  private target: PlayerBike | null = null;

  // Third-person settings
  private readonly TP_DISTANCE = 12;
  private readonly TP_HEIGHT = 5;
  private readonly TP_LOOK_AHEAD = 5;

  // First-person settings
  private readonly FP_HEIGHT = 1.2;
  private readonly FP_FORWARD = 0.5;

  // Smooth following
  private currentPosition = new THREE.Vector3();
  private currentLookAt = new THREE.Vector3();
  private readonly POSITION_LERP = 0.08;
  private readonly LOOKAT_LERP = 0.12;

  // Camera shake
  private shakeIntensity = 0;
  private shakeDuration = 0;
  private shakeTime = 0;

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
  }

  public setTarget(target: PlayerBike): void {
    this.target = target;

    // Initialize camera position
    const pos = target.getPosition();
    if (this.mode === CameraMode.THIRD_PERSON) {
      this.currentPosition.set(pos.x, pos.y + this.TP_HEIGHT, pos.z - this.TP_DISTANCE);
    } else {
      this.currentPosition.copy(pos);
      this.currentPosition.y += this.FP_HEIGHT;
    }
    this.currentLookAt.copy(pos);
  }

  public setMode(mode: CameraMode): void {
    this.mode = mode;
  }

  public toggleMode(): void {
    this.mode =
      this.mode === CameraMode.FIRST_PERSON
        ? CameraMode.THIRD_PERSON
        : CameraMode.FIRST_PERSON;
  }

  public getMode(): CameraMode {
    return this.mode;
  }

  public update(_alpha: number): void {
    if (!this.target) return;

    const targetPos = this.target.getPosition();
    const targetRot = this.target.getRotation();

    // Get forward direction from rotation
    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(targetRot);

    let desiredPosition: THREE.Vector3;
    let desiredLookAt: THREE.Vector3;

    if (this.mode === CameraMode.THIRD_PERSON) {
      // Position behind and above the bike
      const offset = forward.clone().multiplyScalar(-this.TP_DISTANCE);
      offset.y = this.TP_HEIGHT;

      desiredPosition = targetPos.clone().add(offset);
      desiredLookAt = targetPos
        .clone()
        .add(forward.clone().multiplyScalar(this.TP_LOOK_AHEAD));
      desiredLookAt.y += 1; // Look slightly above the bike
    } else {
      // First-person: at rider's head position
      const offset = forward.clone().multiplyScalar(this.FP_FORWARD);
      offset.y = this.FP_HEIGHT;

      desiredPosition = targetPos.clone().add(offset);
      desiredLookAt = desiredPosition
        .clone()
        .add(forward.clone().multiplyScalar(20));
    }

    // Smooth camera movement
    this.currentPosition.lerp(desiredPosition, this.POSITION_LERP);
    this.currentLookAt.lerp(desiredLookAt, this.LOOKAT_LERP);

    // Apply to camera
    this.camera.position.copy(this.currentPosition);

    // Apply camera shake
    if (this.shakeTime > 0) {
      const shakeProgress = this.shakeTime / this.shakeDuration;
      const currentIntensity = this.shakeIntensity * shakeProgress;

      this.camera.position.x += (Math.random() - 0.5) * currentIntensity;
      this.camera.position.y += (Math.random() - 0.5) * currentIntensity;
      this.camera.position.z += (Math.random() - 0.5) * currentIntensity;

      this.shakeTime -= 0.016; // Approximate frame time
    }

    this.camera.lookAt(this.currentLookAt);
  }

  public shake(intensity: number, duration: number): void {
    this.shakeIntensity = intensity;
    this.shakeDuration = duration;
    this.shakeTime = duration;
  }
}
